import { useCallback, useEffect, useMemo } from 'react';
import { useHighlight } from './useHighlight.ts';

type QuestionNotePanelState = 'shown' | 'hidden';

const getQuestionNotePanelStorageKey = (questionId?: number) =>
  questionId ? `question-note-panel:${questionId}` : undefined;

const readQuestionNotePanelState = (
  questionId?: number,
): QuestionNotePanelState | undefined => {
  const storageKey = getQuestionNotePanelStorageKey(questionId);

  if (!storageKey || !window?.localStorage) {
    return undefined;
  }

  try {
    const storedValue = window.localStorage.getItem(storageKey);
    return storedValue === 'shown' || storedValue === 'hidden'
      ? storedValue
      : undefined;
  } catch {
    return undefined;
  }
};

export const setStoredQuestionNotePanelState = (
  questionId: number | undefined,
  visible: boolean,
) => {
  const storageKey = getQuestionNotePanelStorageKey(questionId);

  if (!storageKey || !window?.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, visible ? 'shown' : 'hidden');
  } catch {
    return;
  }
};

export const useQuestionHighlightVisibility = (questionId?: number) => {
  const { highlightData, highlightStickersToggle, setHighlightStickersToggle } =
    useHighlight();

  const hasQuestionStickers = useMemo(
    () =>
      !!questionId &&
      highlightData.some(
        (highlight) =>
          highlight.questionId === questionId &&
          highlight.sticker.isNeedSticker,
      ),
    [highlightData, questionId],
  );

  const isQuestionNotesVisible = hasQuestionStickers && highlightStickersToggle;

  useEffect(() => {
    if (!questionId) {
      setHighlightStickersToggle(false);
      return;
    }

    const storedState = readQuestionNotePanelState(questionId);

    if (storedState === 'shown') {
      setHighlightStickersToggle(hasQuestionStickers);
      return;
    }

    if (storedState === 'hidden') {
      setHighlightStickersToggle(false);
      return;
    }

    setHighlightStickersToggle(hasQuestionStickers);
  }, [hasQuestionStickers, questionId, setHighlightStickersToggle]);

  const setQuestionNotesVisible = useCallback(
    (visible: boolean) => {
      setStoredQuestionNotePanelState(questionId, visible);
      setHighlightStickersToggle(visible && hasQuestionStickers);
    },
    [hasQuestionStickers, questionId, setHighlightStickersToggle],
  );

  const toggleQuestionNotesVisible = useCallback(() => {
    setQuestionNotesVisible(!isQuestionNotesVisible);
  }, [isQuestionNotesVisible, setQuestionNotesVisible]);

  return {
    hasQuestionStickers,
    isQuestionNotesVisible,
    setQuestionNotesVisible,
    toggleQuestionNotesVisible,
  };
};
